import { useEffect, useMemo, useState } from 'react'
import { Search, Smile, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import EmojiGlyph from '@/components/ui/EmojiGlyph'
import { fetchGuildEmojis, type GuildEmoji } from '@/lib/discordEmoji'
import { COMPONENT_GROUP, loadUnicodeEmojis, type PickerEmoji } from '@/lib/unicodeEmoji'
import { useLanguage } from '@/contexts/LanguageContext'

/** Shown before any category is picked — the ones a sign-up position usually wants. */
const COMMON = ['🪖', '🛡️', '🎯', '🚚', '🔧', '💣', '🏳️', '⭐', '🎖️', '🕐', '❌', '🔭']

/** Tab face per emojibase group index; the component group has no tab. */
const GROUP_ICONS: Record<number, string> = {
  0: '😀', 1: '👋', 3: '🐻', 4: '🍔', 5: '✈️', 6: '⚽', 7: '💡', 8: '❤️', 9: '🏁',
}
const GROUPS = Object.keys(GROUP_ICONS).map(Number).filter(g => g !== COMPONENT_GROUP)

/** A search that matches the whole table is not a result list. */
const MAX_RESULTS = 240

/**
 * Picks the icon of a sign-up position: any emoji from our Discord server, or a
 * plain unicode one. The stored value is Discord's own `<:name:id>` text for a
 * guild emoji — see lib/discordEmoji.ts for why that shape and not an id.
 */
export default function EmojiPicker({ value, onChange }: {
  value: string
  onChange: (emoji: string) => void
}) {
  const { T } = useLanguage()
  const [open, setOpen] = useState(false)
  const [emojis, setEmojis] = useState<GuildEmoji[]>([])
  const [unicode, setUnicode] = useState<PickerEmoji[] | null>(null)
  const [group, setGroup] = useState<number | null>(null)
  const [query, setQuery] = useState('')

  // Only once the picker is first opened: most visits to Settings never touch it.
  useEffect(() => {
    if (!open) return
    fetchGuildEmojis().then(setEmojis)
    loadUnicodeEmojis().then(setUnicode).catch(() => {})
  }, [open])

  const q = query.trim().toLowerCase()

  const filtered = useMemo(() => {
    const rows = q ? emojis.filter(e => e.name.toLowerCase().includes(q)) : emojis
    return rows.slice(0, 96)   // a wall of 500 emotes is not a picker
  }, [emojis, q])

  // Search spans every category; without one, the selected tab (or the short
  // default list) is shown. Pasting an emoji straight into the box finds it too.
  const unicodeRows = useMemo<string[]>(() => {
    if (q) {
      if (!unicode) return []
      return unicode
        .filter(e => e.search.includes(q) || e.char === query.trim())
        .slice(0, MAX_RESULTS)
        .map(e => e.char)
    }
    if (group === null || !unicode) return COMMON
    return unicode.filter(e => e.group === group).map(e => e.char)
  }, [unicode, group, q, query])

  const pick = (emoji: string) => { onChange(emoji); setOpen(false); setQuery(''); setGroup(null) }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={T.settings.classEmoji}
          className="flex h-8 w-11 shrink-0 items-center justify-center rounded border border-white/10 bg-white/5 transition-colors hover:border-primary/40 hover:bg-white/8"
        >
          {value
            ? <EmojiGlyph emoji={value} size={18} />
            : <Smile className="h-4 w-4 text-muted-foreground/40" />}
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-80">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/40" />
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={T.settings.emojiSearch}
            className="h-8 w-full rounded border border-white/10 bg-white/5 pl-7 pr-2 text-sm text-foreground placeholder:text-foreground/30 focus:border-primary focus:outline-none"
          />
        </div>

        <p className="mt-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50">
          {T.settings.emojiFromServer}
        </p>
        {filtered.length === 0 ? (
          <p className="py-3 text-center text-xs text-muted-foreground/60">
            {emojis.length === 0 ? T.settings.emojiNone : T.settings.emojiNoMatch}
          </p>
        ) : (
          <div className="grid max-h-44 grid-cols-8 gap-1 overflow-y-auto">
            {filtered.map(e => (
              <button
                key={e.id}
                type="button"
                onClick={() => pick(e.text)}
                title={`:${e.name}:`}
                className="flex h-8 w-8 items-center justify-center rounded transition-colors hover:bg-white/10"
              >
                <EmojiGlyph emoji={e.text} size={20} />
              </button>
            ))}
          </div>
        )}

        <p className="mt-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50">
          {T.settings.emojiUnicode}
        </p>
        {!q && (
          <div className="mb-1.5 flex gap-0.5 border-b border-white/10 pb-1.5">
            {GROUPS.map(g => (
              <button
                key={g}
                type="button"
                onClick={() => setGroup(group === g ? null : g)}
                title={T.settings.emojiGroups[g]}
                className={`flex h-7 flex-1 items-center justify-center rounded text-base leading-none transition-colors hover:bg-white/10 ${
                  group === g ? 'bg-white/10 ring-1 ring-primary/50' : 'opacity-60 hover:opacity-100'
                }`}
              >
                {GROUP_ICONS[g]}
              </button>
            ))}
          </div>
        )}
        {unicodeRows.length === 0 ? (
          <p className="py-3 text-center text-xs text-muted-foreground/60">
            {unicode ? T.settings.emojiNoMatch : T.settings.emojiLoading}
          </p>
        ) : (
        <div className="grid max-h-52 grid-cols-8 gap-1 overflow-y-auto">
          {unicodeRows.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => pick(c)}
              className="flex h-8 w-8 items-center justify-center rounded text-lg leading-none transition-colors hover:bg-white/10"
            >
              {c}
            </button>
          ))}
        </div>
        )}

        {value && (
          <button
            type="button"
            onClick={() => pick('')}
            className="mt-3 flex w-full items-center justify-center gap-1.5 rounded border border-white/10 py-1.5 text-[11px] text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive"
          >
            <X className="h-3 w-3" />
            {T.settings.emojiClear}
          </button>
        )}
      </PopoverContent>
    </Popover>
  )
}
